import {
  FUNDING_PARTNERS,
  FundingPartnerSearchResult,
  FundingPartnerMatch,
  FundingData,
  FundingPartner,
  FundingPartnerSearchResultMatch,
} from './types';

const calculateMonthsInBusiness = (
  startMonth: number | null,
  startYear: number | null
): number | null => {
  if (!startMonth || !startYear) return null;
  const now = new Date();
  const startDate = new Date(startYear, startMonth - 1);
  return (
    (now.getFullYear() - startDate.getFullYear()) * 12 + (now.getMonth() - startDate.getMonth())
  );
};

const evaluateMinRequirement = (
  requirement: number | undefined,
  actual: number | null,
  mismatchMessage: string
): FundingPartnerMatch => {
  if (requirement === undefined) return { isMatch: null, message: 'No minimum requirement' };
  if (actual === null) return { isMatch: null, message: 'Missing input data' };
  const isMatch = actual >= requirement;
  return {
    isMatch,
    message: isMatch ? 'Meets requirement' : mismatchMessage,
  };
};

const evaluateLoanAmountMatch = (
  minLoanAmount: number | undefined,
  maxLoanAmount: number | undefined,
  actual: number | null
): FundingPartnerMatch => {
  if (actual === null) return { isMatch: null, message: 'Loan amount not provided' };

  if (minLoanAmount !== undefined && actual < minLoanAmount) {
    return {
      isMatch: false,
      message: 'The desired loan amount you provided is below the minimum.',
    };
  }

  if (maxLoanAmount !== undefined && actual > maxLoanAmount) {
    return {
      isMatch: false,
      message: 'The desired loan amount you provided exceeds the maximum.',
    };
  }

  return {
    isMatch: true,
    message: 'The desired loan amount falls within the range offered by the partner.',
  };
};

export const searchFundingPartners = (data: FundingData): FundingPartnerSearchResult[] => {
  const monthsInBusiness = calculateMonthsInBusiness(
    data.businessStartMonth,
    data.businessStartYear
  );

  const results: FundingPartnerSearchResult[] = FUNDING_PARTNERS.map((partner: FundingPartner) => {
    const match = {
      minPersonalCreditScore: evaluateMinRequirement(
        partner.requirements.minPersonalCreditScore,
        data.personalCreditScore,
        'The personal credit score you entered is below the minimum required.'
      ),
      minMonthsInBusiness: evaluateMinRequirement(
        partner.requirements.minMonthsInBusiness,
        monthsInBusiness,
        'The number of your months in business is below the minimum required by this partner.'
      ),
      minMonthlyRevenue: { isMatch: null, message: 'Not evaluated.' },
      minAnnualPersonalIncome: {
        isMatch: null,
        message: 'Not evaluated',
      },
      maxDebtToIncomeRatio: {
        isMatch: null,
        message: 'Not evaluated',
      },
      loanAmount: evaluateLoanAmountMatch(
        partner.minLoanAmount,
        partner.maxLoanAmount,
        data.sumOfLoanCustomerNeeds
      ),
    };

    return { fundingPartner: partner, match };
  });

  const score = (match: FundingPartnerMatch) =>
    match.isMatch === null ? 12 : match.isMatch ? 11 : 0;

  const totalScore = (r: FundingPartnerSearchResult) =>
    Object.values(r.match).reduce((sum, m) => sum + score(m), 0) +
    (r.fundingPartner.rank === undefined ? 0 : r.fundingPartner.rank);

  results.sort((a, b) => {
    return totalScore(b) - totalScore(a);
  });

  return results;
};

export const fundingPartnerHasMismatches = (match: FundingPartnerSearchResultMatch) =>
  match.minPersonalCreditScore.isMatch === false ||
  match.minMonthsInBusiness.isMatch === false ||
  match.minMonthlyRevenue.isMatch === false ||
  match.minAnnualPersonalIncome.isMatch === false ||
  match.maxDebtToIncomeRatio.isMatch === false ||
  match.loanAmount.isMatch === false;
